import React from 'react'
import clsx from 'clsx'
import { noop } from 'lodash'
import { useTranslation } from 'react-i18next'

import Button from '../Button'

import usePagination from './usePagination'
import { calculateJumpNumber, PaginateValue } from './domain'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  disabled?: boolean
  isTaskDetail?: boolean
  onChange?: (curr: number) => void
}

/**
 * Primary UI component for user interaction
 */
function Pagination(props: PaginationProps): JSX.Element {
  const { currentPage, onChange, totalPages, disabled, isTaskDetail } = props

  const { collection, prev, next, handlePrev, handleNext, handleFixed } =
    usePagination({
      max: totalPages,
      currentPage,
      handleChange: onChange,
      groupLimit: 4,
      isJumper: true,
    })
  const { collection: newCollection } = usePagination({
    max: totalPages,
    currentPage: 1,
    handleChange: onChange,
    groupLimit: 4,
    isJumper: true,
  })
  const { t } = useTranslation('common')

  const hasActions = (totalPages: number): boolean => totalPages > 1
  const isType = (type: string) => (item: number | string) =>
    typeof item === type
  const isNumber = isType('number')
  const isString = isType('string')

  const handleClickPaginate = (item: PaginateValue) => {
    if (disabled) {
      return null
    }

    if (typeof item === 'string') {
      const modifierCurrentPage = calculateJumpNumber(item, currentPage, 5)

      handleFixed(modifierCurrentPage)

      return null
    }

    handleFixed(item)
  }

  const renderPaginate = () => {
    let collectionList = collection

    if (collection?.includes(0)) {
      collectionList = newCollection
    }

    return (
      <div className={clsx('pagination__circles')}>
        {collectionList?.map((item, index) => {
          return (
            <div
              className={clsx('pagination__circle', 'pagination__center', {
                pagination__number: isNumber(item),
                pagination__jumper: isString(item),
                'pagination__circle--active': item === currentPage,
                'pagination__circle--task-detail': isTaskDetail,
              })}
              onClick={() => handleClickPaginate(item)}
              key={index}
              data-testid={`pagination-button-${isString(item) ? '...' : item}`}
            >
              {isString(item) ? '...' : item}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className='pagination'>
      {hasActions(totalPages) && (
        <Button
          as='link'
          className='pagination__center'
          onClick={() => (!prev || disabled ? noop : handlePrev())}
          disabled={!prev}
        >
          {t('previous.button')}
        </Button>
      )}
      {renderPaginate()}
      {hasActions(totalPages) && (
        <Button
          as='link'
          className='pagination__center'
          onClick={() => (!next || disabled ? noop : handleNext())}
          disabled={!next}
        >
          {t('next.button')}
        </Button>
      )}
    </div>
  )
}

export default Pagination
